import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Users, CalendarCheck, Flask, ClipboardText, Package, Clock, Warning, ShieldCheck, UserCircle, FirstAidKit, FilePdf, FileCsv, CaretDown, ChatCircleText, ArrowRight, EnvelopeSimple, Gear, CheckCircle, X, Stethoscope } from '@phosphor-icons/react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { errText } from '../utils/errors';

import { Badge } from '../components/ui/badge';
import SatisfactionDiagnosticDialog from '../components/study/SatisfactionDiagnosticDialog';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleLabels = {
  director_estudio: 'Director Estudio',
  investigador_principal: 'Inv. Principal', investigador: 'Co-Investigador',
  monitor: 'Monitor', coordinador: 'Coordinador', data_manager: 'Data Manager',
  sondas: 'Sondas', reclutamiento: 'Reclutamiento',
};

const auditActionColors = {
  CREATE: 'bg-emerald-100 text-emerald-700',
  UPDATE: 'bg-blue-100 text-blue-700',
  DELETE: 'bg-red-100 text-red-700',
  ASSIGN_VOLUNTEER: 'bg-indigo-100 text-indigo-700',
  COMPLETE_QUESTIONNAIRE: 'bg-green-100 text-green-700',
  SAVE_QUESTIONNAIRE: 'bg-amber-100 text-amber-700',
  E_SIGNATURE: 'bg-violet-100 text-violet-700',
};

function KpiCard({ icon: Icon, title, value, sub, color = '#1f3e80', testId }) {
  return (
    <Card className="border-stone-200 rounded-sm shadow-none" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] text-stone-400 uppercase tracking-wider font-medium">{title}</p>
            <p className="text-2xl font-heading font-bold mt-1" style={{ color }}>{value}</p>
            {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
          </div>
          <div className="h-9 w-9 rounded-sm flex items-center justify-center" style={{ backgroundColor: color + '12' }}>
            <Icon size={18} style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProgressBar({ pct, color = '#1f3e80', height = 8 }) {
  return (
    <div className="w-full bg-stone-100 rounded-full overflow-hidden" style={{ height }}>
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }}
      />
    </div>
  );
}

export default function StudyDashboard({ studyId }) {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  // URS-F461: qué alerta esta expandida (key: 'ie'|'consent'|'no_show'|'expiring'|null)
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedAE, setExpandedAE] = useState({});
  const [adversePanelOpen, setAdversePanelOpen] = useState(false);
  const [openQueries, setOpenQueries] = useState([]);
  // URS-F373c: stats de envío de satisfacción pendiente (para alerta en dashboard)
  const [satStats, setSatStats] = useState(null);
  // URS-F395: dialog de diagnóstico de envíos de satisfacción (admin only)
  const [satDiagOpen, setSatDiagOpen] = useState(false);
  const { isAdmin } = usePermissions();
  const [queriesPanelOpen, setQueriesPanelOpen] = useState(false);
  // Adverse-event notification state
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [recipientsDialogOpen, setRecipientsDialogOpen] = useState(false);
  const [aeSelected, setAeSelected] = useState({}); // {event_key: bool}
  const [aeSending, setAeSending] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [suggestedRecipients, setSuggestedRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState('');
  const [recipientsSaving, setRecipientsSaving] = useState(false);
  // Per-send recipient picker (within the notify dialog)
  const [recipientSelected, setRecipientSelected] = useState({}); // {email: true}
  const [extraRecipient, setExtraRecipient] = useState('');
  // Sender picker state (default + alternatives + custom)
  const [senderOptions, setSenderOptions] = useState({ default: null, alternatives: [] });
  const [senderChoice, setSenderChoice] = useState(''); // '', 'custom', or email of an alternative
  const [customFromEmail, setCustomFromEmail] = useState('');
  const [customFromName, setCustomFromName] = useState('');

  const lb = lang === 'es' ? {
    title: 'Resumen del estudio',
    volunteers: 'Voluntarios', included: 'Incluidos', excluded: 'Excluidos',
    pending: 'Pendientes', vol_completed: 'Completados', ie_pending: 'I/E pendiente',
    abandono: 'Abandonos', anulados: 'Anulados',
    appointments: 'Citas', scheduled: 'Programadas', confirmed: 'Confirmadas',
    done: 'Realizadas', no_show: 'No presentado', cancelled: 'Canceladas',
    questionnaires: 'Cuestionarios', completed: 'Completados', in_progress: 'En progreso',
    not_started: 'Sin iniciar', progress: 'Progreso', global_progress: 'Progreso global',
    samples: 'Muestras', stock: 'Stock actual', dispensations: 'Dispensaciones',
    expiring: 'Caduca pronto',
    visit_progress: 'Progreso por visita', day: 'Dia',
    timeline: 'Timeline del estudio', days_elapsed: 'dias transcurridos', of: 'de', days: 'dias',
    not_configured: 'Fechas no configuradas',
    alerts: 'Alertas', no_alerts: 'Sin alertas pendientes',
    alert_ie: 'voluntarios pendientes I/E',
    alert_consent: 'consentimientos pendientes',
    alert_no_show: 'citas no presentado',
    alert_expiring: 'muestras por caducar',
    recent_activity: 'Actividad reciente', no_activity: 'Sin actividad registrada',
    staff_title: 'Equipo del estudio', no_staff: 'Sin personal asignado',
    no_data: 'Guarda el estudio primero para ver el dashboard',
    adverse_title: 'Eventos Adversos', no_adverse: 'No se han reportado eventos adversos',
    adverse_count: 'reportados', adverse_vol: 'Voluntario', adverse_visit: 'Visita',
    adverse_question: 'Pregunta', adverse_obs: 'Observacion', adverse_date: 'Fecha',
    adverse_by: 'Registrado por',
    notify_client: 'Notificar al cliente', notified: 'Notificado',
    pending_notif: 'sin notificar', recipients_btn: 'Destinatarios',
    notify_dialog_title: 'Notificar Efectos Adversos al cliente',
    notify_dialog_desc: 'Selecciona los EAs a incluir en el email. Se enviara un unico email con todos los seleccionados a los destinatarios configurados del estudio.',
    select_all: 'Seleccionar todos los pendientes', deselect_all: 'Desmarcar todos',
    no_pending: 'No hay efectos adversos pendientes de notificar',
    no_recipients: 'No hay destinatarios configurados para este estudio',
    open_recipients: 'Configurar destinatarios',
    recipients_dialog_title: 'Destinatarios de notificaciones EA',
    recipients_dialog_desc: 'Direcciones de email del cliente que recibiran las notificaciones de Efectos Adversos para este estudio.',
    recipients_current: 'Destinatarios actuales', recipients_add: 'Anadir email',
    recipients_add_placeholder: 'cliente@ejemplo.com',
    recipients_save: 'Guardar', recipients_remove: 'Quitar',
    recipients_suggested: 'Usuarios del Portal', recipients_use: 'Usar',
    sending: 'Enviando...', send_now: 'Enviar ahora',
    notify_success: 'Notificacion enviada al cliente',
    notify_no_selection: 'Selecciona al menos un efecto adverso',
    notify_error: 'Error enviando la notificacion',
    cancel: 'Cancelar',
  } : {
    title: 'Study overview',
    volunteers: 'Volunteers', included: 'Included', excluded: 'Excluded',
    pending: 'Pending', vol_completed: 'Completed', ie_pending: 'I/E pending',
    abandono: 'Withdrawals', anulados: 'Cancelled',
    appointments: 'Appointments', scheduled: 'Scheduled', confirmed: 'Confirmed',
    done: 'Completed', no_show: 'No-show', cancelled: 'Cancelled',
    questionnaires: 'Questionnaires', completed: 'Completed', in_progress: 'In progress',
    not_started: 'Not started', progress: 'Progress', global_progress: 'Global progress',
    samples: 'Samples', stock: 'Current stock', dispensations: 'Dispensations',
    expiring: 'Expiring soon',
    visit_progress: 'Progress by visit', day: 'Day',
    timeline: 'Study timeline', days_elapsed: 'days elapsed', of: 'of', days: 'days',
    not_configured: 'Dates not configured',
    alerts: 'Alerts', no_alerts: 'No pending alerts',
    alert_ie: 'volunteers pending I/E',
    alert_consent: 'pending consents',
    alert_no_show: 'no-show appointments',
    alert_expiring: 'samples expiring soon',
    recent_activity: 'Recent activity', no_activity: 'No activity recorded',
    staff_title: 'Study team', no_staff: 'No staff assigned',
    no_data: 'Save the study first to see the dashboard',
    adverse_title: 'Adverse Events', no_adverse: 'No adverse events reported',
    adverse_count: 'reported', adverse_vol: 'Volunteer', adverse_visit: 'Visit',
    adverse_question: 'Question', adverse_obs: 'Observation', adverse_date: 'Date',
    adverse_by: 'Recorded by',
    notify_client: 'Notify client', notified: 'Notified',
    pending_notif: 'pending', recipients_btn: 'Recipients',
    notify_dialog_title: 'Notify Adverse Events to client',
    notify_dialog_desc: 'Pick the AEs to include. A single email will be sent to the configured study recipients.',
    select_all: 'Select all pending', deselect_all: 'Clear selection',
    no_pending: 'No pending adverse events',
    no_recipients: 'No recipients configured for this study',
    open_recipients: 'Configure recipients',
    recipients_dialog_title: 'AE notification recipients',
    recipients_dialog_desc: 'Client email addresses that will receive adverse event notifications for this study.',
    recipients_current: 'Current recipients', recipients_add: 'Add email',
    recipients_add_placeholder: 'client@example.com',
    recipients_save: 'Save', recipients_remove: 'Remove',
    recipients_suggested: 'Portal users', recipients_use: 'Use',
    sending: 'Sending...', send_now: 'Send now',
    notify_success: 'Notification sent to client',
    notify_no_selection: 'Select at least one adverse event',
    notify_error: 'Error sending notification',
    cancel: 'Cancel',
  };

  useEffect(() => {
    if (!studyId) return;
    axios.get(`${API_URL}/api/studies/${studyId}/dashboard`, { withCredentials: true })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    axios.get(`${API_URL}/api/studies/${studyId}/queries`, { withCredentials: true })
      .then(r => setOpenQueries((r.data || []).filter(q => q.status !== 'terminada')))
      .catch(() => setOpenQueries([]));
    // URS-F373c: contadores de envío de satisfacción pendiente por causa.
    axios.get(`${API_URL}/api/studies/${studyId}/satisfaction-pending-stats`, { withCredentials: true })
      .then(r => setSatStats(r.data || null))
      .catch(() => setSatStats(null));
  }, [studyId]);

  if (!studyId) {
    return (
      <div className="text-center py-16 bg-stone-50 rounded-sm border border-stone-100">
        <Flask size={36} className="mx-auto text-stone-300 mb-3" />
        <p className="text-sm text-stone-400">{lb.no_data}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-6 w-6 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { volunteers: vol, appointments: appt, questionnaires: q, samples: smp, visit_progress, timeline, recent_audit, staff, consent_pending, adverse_effects } = data;

  const aeList = adverse_effects || [];
  const aePending = aeList.filter(e => !e.notified);

  const reloadDashboard = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/studies/${studyId}/dashboard`, { withCredentials: true });
      setData(r.data);
    } catch (_e) { /* noop */ }
  };

  const openNotifyDialog = async (preselectKeys) => {
    // If a specific list of keys is passed, only those are pre-checked.
    let keys = preselectKeys;
    if (!keys) keys = aePending.filter(e => e.event_key).map(e => e.event_key);
    const sel = {};
    keys.forEach(k => { sel[k] = true; });
    setAeSelected(sel);
    // Verify recipients exist
    try {
      const r = await axios.get(`${API_URL}/api/studies/${studyId}/adverse-events/recipients`, { withCredentials: true });
      const rec = r.data?.recipients || [];
      setRecipients(rec);
      setSuggestedRecipients(r.data?.suggested_portal_users || []);
      // Pre-check all configured recipients
      const sels = {};
      rec.forEach(e => { sels[e] = true; });
      setRecipientSelected(sels);
    } catch (_e) {
      setRecipients([]);
      setRecipientSelected({});
    }
    setExtraRecipient('');
    // Load sender options
    try {
      const r = await axios.get(`${API_URL}/api/integrations/acumbamail/senders`, { withCredentials: true });
      setSenderOptions({ default: r.data?.default || null, alternatives: r.data?.alternatives || [] });
    } catch (_e) {
      setSenderOptions({ default: null, alternatives: [] });
    }
    setSenderChoice(''); setCustomFromEmail(''); setCustomFromName('');
    setNotifyDialogOpen(true);
  };

  const resolveFromOverride = () => {
    if (senderChoice === 'custom') {
      const email = (customFromEmail || '').trim();
      if (!email.includes('@')) return null;
      return { from_email: email, from_name: (customFromName || '').trim() || email.split('@')[0] };
    }
    if (senderChoice && senderChoice !== '') {
      const alt = senderOptions.alternatives.find(a => a.email === senderChoice);
      if (alt) return { from_email: alt.email, from_name: alt.name };
    }
    return null;
  };

  const sendNotification = async () => {
    const keys = Object.entries(aeSelected).filter(([_k, v]) => v).map(([k]) => k);
    if (keys.length === 0) {
      toast.error(lb.notify_no_selection);
      return;
    }
    // Build effective recipients list (configured selected + extra)
    const effectiveRecipients = [];
    Object.entries(recipientSelected).forEach(([email, on]) => {
      if (on && email && !effectiveRecipients.includes(email)) effectiveRecipients.push(email);
    });
    const extra = (extraRecipient || '').trim().toLowerCase();
    if (extra.includes('@') && !effectiveRecipients.includes(extra)) effectiveRecipients.push(extra);
    if (effectiveRecipients.length === 0) {
      toast.error(lang === 'es' ? 'Selecciona o anade al menos un destinatario' : 'Select or add at least one recipient');
      return;
    }
    if (senderChoice === 'custom' && !customFromEmail.includes('@')) {
      toast.error(lang === 'es' ? 'Email remitente invalido' : 'Invalid sender email');
      return;
    }
    setAeSending(true);
    try {
      const override = resolveFromOverride();
      const r = await axios.post(`${API_URL}/api/studies/${studyId}/adverse-events/notify`,
        { event_keys: keys, recipients: effectiveRecipients, ...(override || {}) },
        { withCredentials: true });
      toast.success(`${lb.notify_success} (${r.data?.sent || 0})`);
      setNotifyDialogOpen(false);
      await reloadDashboard();
    } catch (e) {
      toast.error(errText(e, lb.notify_error));
    } finally {
      setAeSending(false);
    }
  };

  const openRecipientsDialog = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/studies/${studyId}/adverse-events/recipients`, { withCredentials: true });
      setRecipients(r.data?.recipients || []);
      setSuggestedRecipients(r.data?.suggested_portal_users || []);
    } catch (_e) {
      setRecipients([]);
    }
    setNewRecipient('');
    setRecipientsDialogOpen(true);
  };

  const saveRecipients = async (list) => {
    setRecipientsSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/studies/${studyId}/adverse-events/recipients`,
        { recipients: list }, { withCredentials: true });
      setRecipients(r.data?.recipients || []);
      toast.success('OK');
    } catch (e) {
      toast.error(errText(e, 'Error'));
    } finally {
      setRecipientsSaving(false);
    }
  };

  const notifySingle = async (event_key) => {
    // Open the dialog pre-selecting only this row.
    await openNotifyDialog([event_key]);
  };

  // Build alerts
  // URS-F461: cada alerta incluye la key para poder recuperar el detalle.
  const alertDetails = data?.alert_details || {};
  const alerts = [];
  if (vol.ie_pending > 0) alerts.push({ key: 'ie', color: 'amber', text: `${vol.ie_pending} ${lb.alert_ie}`, items: alertDetails.ie_pending_vols || [] });
  if (consent_pending > 0) alerts.push({ key: 'consent', color: 'red', text: `${consent_pending} ${lb.alert_consent}`, items: alertDetails.consent_pending_vols || [] });
  if (appt.no_show > 0) alerts.push({ key: 'no_show', color: 'orange', text: `${appt.no_show} ${lb.alert_no_show}`, items: alertDetails.no_show_appts || [] });
  if (smp.expiring_soon > 0) alerts.push({ key: 'expiring', color: 'amber', text: `${smp.expiring_soon} ${lb.alert_expiring}`, items: alertDetails.expiring_samples || [] });

  const apptStatusLabels = lang === 'es'
    ? { citada: 'Citadas', anulada: 'Anuladas', no_asiste: 'No asiste', sala_espera: 'Sala espera', especialista: 'Especialista', sondas: 'Sondas', terminada: 'Terminadas' }
    : { citada: 'Scheduled', anulada: 'Cancelled', no_asiste: 'No-show', sala_espera: 'Waiting', especialista: 'Specialist', sondas: 'Probes', terminada: 'Completed' };

  return (
    <div className="space-y-5" data-testid="study-dashboard">
      {/* URS-F373c: alerta de cuestionarios de satisfacción pendientes por causa */}
      {satStats && (satStats.exhausted + satStats.no_email + satStats.send_email_off + satStats.invalid_status) > 0 && (
        <div className="border-l-4 border-amber-500 bg-amber-50 rounded-sm px-4 py-3" data-testid="sat-pending-alert">
          <div className="flex items-start gap-3">
            <Warning size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                {lang === 'es' ? 'Envío de cuestionarios de satisfacción con incidencias (próximos 7 días)' : 'Satisfaction questionnaire sending issues (next 7 days)'}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-amber-800">
                {satStats.exhausted > 0 && <span data-testid="sat-alert-exhausted"><strong>{satStats.exhausted}</strong> {lang === 'es' ? 'agotados (4 intentos fallidos)' : 'exhausted (4 failed attempts)'}</span>}
                {satStats.no_email > 0 && <span data-testid="sat-alert-no-email"><strong>{satStats.no_email}</strong> {lang === 'es' ? 'sin email de voluntario' : 'volunteer email missing'}</span>}
                {satStats.send_email_off > 0 && <span data-testid="sat-alert-toggle-off"><strong>{satStats.send_email_off}</strong> {lang === 'es' ? 'con envío automático desactivado' : 'auto-send toggled off'}</span>}
                {satStats.invalid_status > 0 && <span data-testid="sat-alert-invalid-status"><strong>{satStats.invalid_status}</strong> {lang === 'es' ? 'con cita en estado no elegible (anulada/no_presentada)' : 'appointment in non-eligible status'}</span>}
                {satStats.pending > 0 && <span className="text-amber-600">· <strong>{satStats.pending}</strong> {lang === 'es' ? 'pendientes (se enviarán)' : 'pending (will be sent)'}</span>}
              </div>
              {isAdmin && (
                <div className="mt-2">
                  <button
                    type="button"
                    data-testid="sat-diag-open-alert"
                    onClick={() => setSatDiagOpen(true)}
                    className="text-xs inline-flex items-center gap-1 text-[#1f3e80] hover:underline font-semibold"
                  >
                    <Stethoscope size={12} /> {lang === 'es' ? 'Diagnosticar envíos' : 'Diagnose sending'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* URS-F395: acceso siempre visible (admin) al diálogo de diagnóstico */}
      {isAdmin && !(satStats && (satStats.exhausted + satStats.no_email + satStats.send_email_off + satStats.invalid_status) > 0) && (
        <div className="flex justify-end">
          <button
            type="button"
            data-testid="sat-diag-open"
            onClick={() => setSatDiagOpen(true)}
            className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 border border-stone-300 rounded-sm bg-white text-stone-700 hover:bg-blue-50 hover:border-[#1f3e80] hover:text-[#1f3e80] transition-colors"
          >
            <Stethoscope size={14} /> {lang === 'es' ? 'Diagnóstico / Historial de envíos de satisfacción' : 'Satisfaction sending diagnostic / history'}
          </button>
        </div>
      )}
      <SatisfactionDiagnosticDialog
        studyId={studyId}
        open={satDiagOpen}
        onOpenChange={setSatDiagOpen}
      />
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={Users} title={lb.volunteers} value={vol.included}
          sub={vol.target_participants != null
            ? `${vol.target_participants} ${lang === 'es' ? 'total' : 'target'}`
            : (lang === 'es' ? 'Sin objetivo definido' : 'No target set')}
          color="#1f3e80" testId="kpi-volunteers"
        />
        <KpiCard
          icon={CalendarCheck} title={lb.appointments} value={appt.done}
          sub={`${appt.total} ${lang === 'es' ? 'total' : 'total'}`}
          color="#1D4ED8" testId="kpi-appointments"
        />
        <KpiCard
          icon={ClipboardText} title={lb.questionnaires} value={`${q.pct}%`}
          sub={`${q.completed}/${q.total} ${lb.completed.toLowerCase()}`}
          color="#7C3AED" testId="kpi-questionnaires"
        />
        <KpiCard
          icon={Package} title={lb.samples} value={smp.count}
          sub={(smp.per_sample_stock && smp.per_sample_stock.length > 0)
            ? `${lb.stock}: ${smp.per_sample_stock.map(x => `${x.stock_remaining}/${x.quantity_received}`).join(' · ')}`
            : (lang === 'es' ? 'Sin muestras' : 'No samples')}
          color="#B45309" testId="kpi-samples"
        />
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50 rounded-sm shadow-none" data-testid="study-alerts">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Warning size={16} className="text-amber-600" />
              <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">{lb.alerts}</span>
            </div>
            {/* URS-F461: chips clicables que expanden el detalle. */}
            <div className="flex flex-wrap gap-2">
              {alerts.map(a => {
                const isOpen = expandedAlert === a.key;
                return (
                  <button
                    key={a.key}
                    type="button"
                    data-testid={`study-alert-chip-${a.key}`}
                    onClick={() => setExpandedAlert(isOpen ? null : a.key)}
                    className={`text-xs px-2.5 py-1 rounded border transition-colors flex items-center gap-1.5 ${
                      isOpen
                        ? `bg-${a.color}-100 border-${a.color}-500 text-${a.color}-800 font-semibold`
                        : `bg-${a.color}-50 border-${a.color}-300 text-${a.color}-700 hover:bg-${a.color}-100`
                    }`}
                  >
                    {a.text}
                    <span className="text-[10px] opacity-70">{isOpen ? '▲' : '▼'}</span>
                  </button>
                );
              })}
            </div>
            {/* Detalle expandido */}
            {expandedAlert && (
              <div className="mt-3 bg-white border border-stone-200 rounded-sm p-3" data-testid={`study-alert-detail-${expandedAlert}`}>
                {(() => {
                  const alert = alerts.find(a => a.key === expandedAlert);
                  if (!alert || alert.items.length === 0) {
                    return <p className="text-xs text-stone-500 italic">{lang === 'es' ? 'Sin detalle disponible.' : 'No detail available.'}</p>;
                  }
                  if (expandedAlert === 'ie' || expandedAlert === 'consent') {
                    return (
                      <ul className="divide-y divide-stone-100 text-xs">
                        {alert.items.map((item, i) => (
                          <li key={i} className="py-1.5 flex justify-between items-center gap-3" data-testid={`study-alert-item-${expandedAlert}-${i}`}>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="font-mono text-[11px] text-stone-500 flex-shrink-0">
                                {item.study_volunteer_code || '—'}
                              </span>
                              <span className="text-stone-700 truncate">{item.volunteer_name || item.volunteer_id}</span>
                              {expandedAlert === 'consent' && item.missing_templates?.length > 0 && (
                                <span className="text-[10px] text-red-600 truncate">
                                  {item.missing_templates.join(', ')}
                                </span>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-[11px] text-[#1f3e80] hover:bg-blue-50"
                              onClick={() => {
                                // URS-F469d: navegacion contextual segun tipo de alerta.
                                // - I/E pendiente -> subpestana Pendientes de voluntarios del estudio.
                                // - Consentimientos pendientes -> pestana Consentimientos con
                                //   Seguimiento auto-desplegado en las CI faltantes.
                                if (expandedAlert === 'ie') {
                                  navigate(`/studies/${studyId}?tab=volunteers&volunteers_tab=pendientes&highlight_volunteer=${item.volunteer_id}`);
                                } else {
                                  navigate(`/studies/${studyId}?tab=consents&highlight_volunteer=${item.volunteer_id}`);
                                }
                              }}
                            >
                              {lang === 'es' ? 'Abrir' : 'Open'}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  if (expandedAlert === 'no_show') {
                    return (
                      <ul className="divide-y divide-stone-100 text-xs">
                        {alert.items.map((item, i) => (
                          <li key={i} className="py-1.5 flex justify-between items-center gap-3" data-testid={`study-alert-item-no_show-${i}`}>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="font-mono text-[11px] text-stone-500 flex-shrink-0">
                                {item.study_volunteer_code || '—'}
                              </span>
                              <span className="text-stone-700 truncate">{item.volunteer_name || item.volunteer_id}</span>
                              <span className="text-[11px] text-stone-500">{item.visit_code} · {item.date} {item.time}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-[11px] text-[#1f3e80] hover:bg-blue-50"
                              onClick={() => navigate(`/volunteers/${item.volunteer_id}`)}
                            >
                              {lang === 'es' ? 'Abrir' : 'Open'}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  if (expandedAlert === 'expiring') {
                    return (
                      <ul className="divide-y divide-stone-100 text-xs">
                        {alert.items.map((item, i) => (
                          <li key={i} className="py-1.5 flex justify-between items-center gap-3" data-testid={`study-alert-item-expiring-${i}`}>
                            <div className="flex-1 min-w-0">
                              <span className="text-stone-700 font-medium truncate">{item.name}</span>
                              {item.batch && <span className="ml-2 text-[11px] text-stone-500">Lote: {item.batch}</span>}
                              {item.current_stock !== undefined && (
                                <span className="ml-2 text-[11px] text-stone-500">Stock: {item.current_stock}</span>
                              )}
                            </div>
                            <span className="text-[11px] text-amber-700 font-semibold flex-shrink-0">
                              {lang === 'es' ? 'Caduca' : 'Expires'}: {item.expiry_date}
                            </span>
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Queries en curso */}
      {openQueries.length > 0 && (
        <Card className="border-amber-300 bg-white rounded-sm shadow-none" data-testid="study-queries-panel">
          <CardContent className="p-0">
            <button type="button" data-testid="study-queries-toggle"
              onClick={() => setQueriesPanelOpen(p => !p)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-amber-50/50 transition-colors text-left">
              <div className="flex items-center gap-2">
                <ChatCircleText size={16} className="text-amber-700" />
                <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
                  {lang === 'es' ? 'Queries en curso' : 'Open Queries'}
                </span>
                <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300 font-mono">
                  {openQueries.length} {lang === 'es' ? 'pendientes' : 'pending'}
                </Badge>
              </div>
              <CaretDown size={14} className={`text-amber-400 transition-transform duration-200 ${queriesPanelOpen ? 'rotate-180' : ''}`} />
            </button>
            {queriesPanelOpen && (
              <div className="border-t border-amber-200 divide-y divide-stone-100">
                {openQueries.map(q => (
                  <button
                    key={q.query_id}
                    type="button"
                    data-testid={`study-dashboard-query-${q.code}`}
                    onClick={() => navigate(`/studies/${studyId}?tab=queries&query_id=${q.query_id}`)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-amber-50/50 transition-colors text-left"
                  >
                    <Badge variant="outline" className="text-[10px] font-mono bg-white shrink-0">{q.code}</Badge>
                    <div className="flex items-center gap-2 text-xs flex-1 min-w-0">
                      <span className="font-medium text-stone-700 shrink-0">
                        {q.target_role === 'investigador_principal' ? (lang === 'es' ? 'Investigador Principal' : 'Principal Investigator')
                          : q.target_role === 'reclutamiento' ? (lang === 'es' ? 'Reclutamiento' : 'Recruitment')
                          : (lang === 'es' ? 'Sondas' : 'Probes')}
                      </span>
                      <span className="text-stone-400">·</span>
                      <span className="font-mono text-stone-500">{q.study_volunteer_code}</span>
                      <span className="text-stone-400">·</span>
                      <span className="font-mono text-stone-500">{q.visit_code}</span>
                      <span className="text-stone-400 ml-auto shrink-0">
                        {new Date(q.created_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-GB')}
                      </span>
                    </div>
                    <ArrowRight size={12} className="text-stone-300 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Adverse Effects Panel - right after Alerts */}
      {aeList.length > 0 && (
        <Card className="border-red-200 bg-red-50/30 rounded-sm shadow-none" data-testid="study-adverse-effects">
          <CardContent className="p-0">
            <button type="button" data-testid="adverse-panel-toggle"
              onClick={() => setAdversePanelOpen(p => !p)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-red-50/50 transition-colors text-left">
              <div className="flex items-center gap-2 flex-wrap">
                <FirstAidKit size={16} className="text-red-600" />
                <span className="text-xs font-semibold text-red-800 uppercase tracking-wider">{lb.adverse_title}</span>
                <Badge variant="outline" className="text-xs bg-red-100 text-red-700 border-red-300 font-mono">
                  {aeList.length} {lb.adverse_count}
                </Badge>
                {aePending.length > 0 && (
                  <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300 font-mono" data-testid="adverse-pending-badge">
                    {aePending.length} {lb.pending_notif}
                  </Badge>
                )}
              </div>
              <CaretDown size={14} className={`text-red-400 transition-transform duration-200 ${adversePanelOpen ? 'rotate-180' : ''}`} />
            </button>
            {adversePanelOpen && (
              <div className="px-4 pb-4 border-t border-red-200">
                <div className="flex justify-end gap-1 mt-2 mb-3 flex-wrap">
                  <Button variant="outline" size="sm" data-testid="adverse-notify-client-btn"
                    className="h-7 px-3 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 gap-1"
                    onClick={(e) => { e.stopPropagation(); openNotifyDialog(); }}
                    disabled={aePending.length === 0}>
                    <EnvelopeSimple size={14} /> {lb.notify_client}
                    {aePending.length > 0 && <span className="font-mono ml-1">({aePending.length})</span>}
                  </Button>
                  <Button variant="outline" size="sm" data-testid="adverse-recipients-btn"
                    className="h-7 px-3 gap-1"
                    onClick={(e) => { e.stopPropagation(); openRecipientsDialog(); }}>
                    <Gear size={14} /> {lb.recipients_btn}
                  </Button>
                  <Button variant="ghost" size="sm" data-testid="adverse-export-pdf"
                    className="h-7 px-2 text-red-700 hover:bg-red-100 gap-1"
                    onClick={() => {
                      window.open(`${API_URL}/api/studies/${studyId}/adverse-effects/export?format=pdf`, '_blank');
                    }}>
                    <FilePdf size={14} /> PDF
                  </Button>
                  <Button variant="ghost" size="sm" data-testid="adverse-export-csv"
                    className="h-7 px-2 text-red-700 hover:bg-red-100 gap-1"
                    onClick={() => {
                      window.open(`${API_URL}/api/studies/${studyId}/adverse-effects/export?format=csv`, '_blank');
                    }}>
                    <FileCsv size={14} /> CSV
                  </Button>
                </div>
                <div className="space-y-2">
              {aeList.map((ae, i) => {
                const isOpen = !!expandedAE[i];
                return (
                  <div key={i} data-testid={`adverse-effect-${i}`} className="bg-white border border-red-200 rounded-sm overflow-hidden">
                    <button type="button" data-testid={`adverse-toggle-${i}`}
                      onClick={() => setExpandedAE(p => ({ ...p, [i]: !p[i] }))}
                      className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-red-50/50 transition-colors text-left">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] bg-stone-50 text-stone-700 border-stone-300 font-mono">{ae.volunteer_code}</Badge>
                        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200 font-mono">{ae.visit_code}</Badge>
                        <span className="text-[10px] text-stone-400">{ae.questionnaire}</span>
                        {ae.notified ? (
                          <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200 gap-1" data-testid={`adverse-notified-${i}`}>
                            <CheckCircle size={10} weight="fill" /> {lb.notified}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200" data-testid={`adverse-not-notified-${i}`}>
                            {lb.pending_notif}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {!ae.notified && ae.event_key && (
                          <Button size="sm" variant="ghost" data-testid={`adverse-notify-row-${i}`}
                            className="h-6 px-2 text-amber-700 hover:bg-amber-100 gap-1 text-[11px]"
                            onClick={(e) => { e.stopPropagation(); notifySingle(ae.event_key); }}>
                            <EnvelopeSimple size={12} /> {lb.notify_client}
                          </Button>
                        )}
                        <span className="text-[10px] text-stone-400">
                          {ae.date ? new Date(ae.date).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                        </span>
                        <CaretDown size={12} className={`text-stone-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-3 pb-3 border-t border-red-100 pt-2 space-y-1.5" data-testid={`adverse-detail-${i}`}>
                        <p className="text-xs font-medium text-stone-700">{ae.question}</p>
                        {/* URS-F296: para Tolerancia mostrar solo las filas afectadas (valor > 0) con su relacion con producto. */}
                        {Array.isArray(ae.rows) && ae.rows.length > 0 ? (
                          <div className="bg-red-50 border border-red-100 rounded-sm px-3 py-2 space-y-1">
                            {ae.rows.map((r, ri) => (
                              <div key={ri} className="flex items-center justify-between text-xs">
                                <span className="text-red-900"><strong>{r.row}</strong>: {r.value}</span>
                                {r.relation && (
                                  <span className="text-[10px] text-stone-600 bg-white border border-stone-200 rounded-sm px-1.5 py-0.5">
                                    {lang === 'es' ? 'Relación' : 'Relation'}: {r.relation}
                                  </span>
                                )}
                              </div>
                            ))}
                            {ae.observation && (
                              <p className="text-[11px] text-stone-600 italic pt-1 border-t border-red-100">{ae.observation}</p>
                            )}
                          </div>
                        ) : (
                          <div className="bg-red-50 border border-red-100 rounded-sm px-3 py-2">
                            <p className="text-xs text-red-900 leading-relaxed">{ae.observation || '-'}</p>
                          </div>
                        )}
                        {ae.notified && ae.notified_at && (
                          <p className="text-[10px] text-green-700 text-right">
                            {lb.notified}: {new Date(ae.notified_at).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')} → {(ae.notified_to || []).join(', ')}
                          </p>
                        )}
                        {ae.signed_by && <p className="text-[10px] text-stone-400 text-right">{ae.signed_by}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Timeline + Volunteer breakdown row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Timeline */}
        <Card className="border-stone-200 rounded-sm shadow-none" data-testid="study-timeline">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={16} className="text-[#1f3e80]" />
              <span className="text-xs font-semibold text-stone-700 uppercase tracking-wider">{lb.timeline}</span>
            </div>
            {timeline.start_date && timeline.end_date ? (
              <div className="space-y-4">
                {/* URS-F470: Teorico */}
                <div data-testid="timeline-teorico">
                  <p className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">
                    {lang === 'es' ? 'Teorico' : 'Theoretical'}
                  </p>
                  <ProgressBar pct={timeline.pct} />
                  <div className="flex justify-between mt-2">
                    <span className="text-[11px] text-stone-500">{timeline.start_date}</span>
                    <span className="text-[11px] font-medium text-stone-600">
                      {timeline.days_elapsed > 0 ? `${timeline.days_elapsed} ${lb.days_elapsed}` : ''} ({timeline.pct}%)
                    </span>
                    <span className="text-[11px] text-stone-500">{timeline.end_date}</span>
                  </div>
                  <p className="text-[11px] text-stone-400 mt-1 text-center">
                    {timeline.days_elapsed} {lb.of} {timeline.days_total} {lb.days}
                  </p>
                </div>

                {/* URS-F470: Real */}
                <div data-testid="timeline-real">
                  <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider mb-1">
                    {lang === 'es' ? 'Real' : 'Real'}
                    {timeline.last_vcode && timeline.last_visit_code && (
                      <span className="ml-2 text-[10px] font-normal text-stone-400 normal-case tracking-normal">
                        · {timeline.last_vcode} @ {timeline.last_visit_code}
                      </span>
                    )}
                  </p>
                  {timeline.real_end_date ? (
                    <>
                      <ProgressBar pct={timeline.real_pct} />
                      <div className="flex justify-between mt-2">
                        <span className="text-[11px] text-stone-500">{timeline.start_date}</span>
                        <span className="text-[11px] font-medium text-emerald-700">
                          {timeline.real_days_elapsed > 0 ? `${timeline.real_days_elapsed} ${lb.days_elapsed}` : ''} ({timeline.real_pct}%)
                        </span>
                        <span className="text-[11px] text-stone-500">{timeline.real_end_date}</span>
                      </div>
                      <p className="text-[11px] text-stone-400 mt-1 text-center">
                        {timeline.real_days_elapsed} {lb.of} {timeline.real_days_total} {lb.days}
                      </p>
                    </>
                  ) : (
                    <p className="text-[11px] text-stone-400 italic text-center py-2">
                      {lang === 'es'
                        ? 'Sin citas del ultimo V-code en la visita final del protocolo'
                        : 'No appointments of the last V-code at the final protocol visit'}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-xs text-stone-400 text-center py-3">{lb.not_configured}</p>
            )}
          </CardContent>
        </Card>

        {/* Volunteer breakdown */}
        <Card className="border-stone-200 rounded-sm shadow-none" data-testid="study-vol-breakdown">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users size={16} className="text-[#1f3e80]" />
              <span className="text-xs font-semibold text-stone-700 uppercase tracking-wider">{lb.volunteers}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: 'pending',    label: lb.pending,       val: vol.pendiente_strict ?? vol.pending, cls: 'bg-amber-50 border-amber-100 text-amber-700 subcls-amber-600' },
                { key: 'included',   label: lb.included,      val: vol.included_strict ?? vol.included, cls: 'bg-emerald-50 border-emerald-100 text-emerald-700 subcls-emerald-600' },
                { key: 'completed',  label: lb.vol_completed, val: vol.completed,                       cls: 'bg-blue-50 border-blue-100 text-blue-700 subcls-blue-600' },
                { key: 'excluded',   label: lb.excluded,      val: vol.excluded,                        cls: 'bg-red-50 border-red-100 text-red-700 subcls-red-600' },
                { key: 'abandono',   label: lb.abandono,      val: vol.abandono ?? 0,                   cls: 'bg-orange-50 border-orange-100 text-orange-700 subcls-orange-600' },
                { key: 'anulados',   label: lb.anulados,      val: vol.anulados ?? 0,                   cls: 'bg-rose-50 border-rose-100 text-rose-700 subcls-rose-600' },
              ].map(item => {
                // URS-F469: clases explicitas para que Tailwind JIT las
                // detecte en produccion (no depender de template strings).
                const [bg, border, textPrimary, subMarker] = item.cls.split(' ');
                const textSub = subMarker.replace('subcls-', 'text-');
                return (
                  <div key={item.key} data-testid={`vol-breakdown-${item.key}`} className={`${bg} border ${border} rounded-sm px-3 py-2`}>
                    <p className={`text-lg font-bold ${textPrimary}`}>{item.val}</p>
                    <p className={`text-[11px] ${textSub}`}>{item.label}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Appointment status + Questionnaire progress row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Appointment status */}
        <Card className="border-stone-200 rounded-sm shadow-none" data-testid="study-appt-status">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <CalendarCheck size={16} className="text-[#1D4ED8]" />
              <span className="text-xs font-semibold text-stone-700 uppercase tracking-wider">{lb.appointments}</span>
            </div>
            {appt.total > 0 ? (
              <div className="space-y-2">
                {Object.entries(apptStatusLabels).map(([key, label]) => {
                  const count = appt.by_status[key] || 0;
                  const pct = appt.total > 0 ? Math.round(count / appt.total * 100) : 0;
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <span className="text-[11px] text-stone-500 w-24 text-right">{label}</span>
                      <div className="flex-1">
                        <ProgressBar pct={pct} color={key === 'terminada' ? '#059669' : key === 'no_asiste' ? '#DC2626' : key === 'anulada' ? '#9CA3AF' : key === 'sala_espera' ? '#D97706' : key === 'especialista' ? '#7C3AED' : key === 'sondas' ? '#0D9488' : '#1f3e80'} height={6} />
                      </div>
                      <span className="text-xs font-mono text-stone-600 w-10 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-stone-400 text-center py-3">{lang === 'es' ? 'Sin citas' : 'No appointments'}</p>
            )}
          </CardContent>
        </Card>

        {/* Questionnaire progress */}
        <Card className="border-stone-200 rounded-sm shadow-none" data-testid="study-q-progress">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardText size={16} className="text-[#7C3AED]" />
              <span className="text-xs font-semibold text-stone-700 uppercase tracking-wider">{lb.global_progress}</span>
            </div>
            {q.total > 0 ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold text-[#7C3AED]">{q.pct}%</span>
                  <span className="text-xs text-stone-500">{q.completed}/{q.total}</span>
                </div>
                <ProgressBar pct={q.pct} color="#7C3AED" />
                <div className="flex gap-3 mt-3">
                  <div className="flex items-center gap-1.5">
                    <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
                    <span className="text-[11px] text-stone-500">{q.completed} {lb.completed}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                    <span className="text-[11px] text-stone-500">{q.in_progress} {lb.in_progress}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
                    <span className="text-[11px] text-stone-500">{q.not_started} {lb.not_started}</span>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-xs text-stone-400 text-center py-3">{lang === 'es' ? 'Sin cuestionarios asignados' : 'No questionnaires assigned'}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Visit Progress */}
      {visit_progress.length > 0 && (
        <Card className="border-stone-200 rounded-sm shadow-none" data-testid="study-visit-progress">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <CalendarCheck size={16} className="text-[#1f3e80]" />
              <span className="text-xs font-semibold text-stone-700 uppercase tracking-wider">{lb.visit_progress}</span>
            </div>
            <div className="space-y-2.5">
              {visit_progress.map(vp => (
                <div key={vp.code} className="flex items-center gap-3">
                  <div className="w-24 text-right">
                    <span className="text-xs font-mono font-semibold text-stone-700">{vp.code}</span>
                    <span className="text-[10px] text-stone-400 ml-1">({lb.day} {vp.day})</span>
                  </div>
                  <div className="flex-1">
                    <ProgressBar pct={vp.pct} color="#1f3e80" height={6} />
                  </div>
                  <span className="text-[11px] text-stone-500 w-20 text-right">
                    {vp.done}/{vp.total} ({vp.pct}%)
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Staff + Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Staff */}
        <Card className="border-stone-200 rounded-sm shadow-none" data-testid="study-staff-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <UserCircle size={16} className="text-[#1f3e80]" />
              <span className="text-xs font-semibold text-stone-700 uppercase tracking-wider">{lb.staff_title}</span>
            </div>
            {staff.length > 0 ? (
              <div className="space-y-1.5">
                {staff.map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-stone-50 last:border-0">
                    <div>
                      <span className="text-sm text-stone-800">{s.name}</span>
                      <span className="text-[11px] text-stone-400 ml-2">{s.email}</span>
                    </div>
                    <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                      {roleLabels[s.role] || s.role}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-stone-400 text-center py-3">{lb.no_staff}</p>
            )}
          </CardContent>
        </Card>

        {/* Recent activity */}
        <Card className="border-stone-200 rounded-sm shadow-none" data-testid="study-recent-activity">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={16} className="text-[#1f3e80]" />
              <span className="text-xs font-semibold text-stone-700 uppercase tracking-wider">{lb.recent_activity}</span>
            </div>
            {recent_audit.length > 0 ? (
              <div className="space-y-1.5 max-h-52 overflow-y-auto">
                {recent_audit.map((log, i) => (
                  <div key={i} className="flex items-start gap-2 py-1.5 border-b border-stone-50 last:border-0">
                    <Badge className={`text-[9px] shrink-0 mt-0.5 ${auditActionColors[log.action] || 'bg-stone-100 text-stone-600'}`}>
                      {log.action}
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] text-stone-600 truncate">{log.user_email}</p>
                      <p className="text-[10px] text-stone-400">{new Date(log.timestamp).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-stone-400 text-center py-3">{lb.no_activity}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ===== Notify Adverse Events Dialog ===== */}
      <Dialog open={notifyDialogOpen} onOpenChange={setNotifyDialogOpen}>
        <DialogContent data-testid="adverse-notify-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{lb.notify_dialog_title}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-500">{lb.notify_dialog_desc}</p>
          <div className="rounded-sm border border-stone-200 p-2 bg-white text-[11px] space-y-1.5" data-testid="ae-recipients-picker">
            <div className="flex items-center justify-between">
              <span className="text-stone-700 font-medium">{lang === 'es' ? 'Destinatarios' : 'Recipients'}</span>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={openRecipientsDialog}>
                <Gear size={11} className="mr-1" /> {lang === 'es' ? 'Configurar lista' : 'Configure list'}
              </Button>
            </div>
            {recipients.length === 0 ? (
              <p className="text-stone-400 italic text-[10px]">{lang === 'es' ? 'Sin destinatarios configurados. Anade uno abajo o configura la lista.' : 'No recipients configured. Add one below or configure the list.'}</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {recipients.map(em => (
                  <label key={em} className="flex items-center gap-1 bg-stone-50 hover:bg-stone-100 border border-stone-200 rounded-sm px-1.5 py-0.5 cursor-pointer" data-testid={`ae-recipient-${em}`}>
                    <Checkbox
                      checked={!!recipientSelected[em]}
                      onCheckedChange={(v) => setRecipientSelected(p => ({ ...p, [em]: !!v }))}
                      data-testid={`ae-recipient-cb-${em}`}
                    />
                    <span className="font-mono text-[10px]">{em}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex gap-1.5 pt-1">
              <Input
                value={extraRecipient}
                onChange={(e) => setExtraRecipient(e.target.value)}
                placeholder={lang === 'es' ? 'Otro email (envio puntual, no se guarda)' : 'Other email (one-off, not saved)'}
                type="email"
                className="h-7 text-xs"
                data-testid="ae-recipient-extra"
              />
            </div>
          </div>
          {/* Sender picker */}
          <div className="rounded-sm border border-stone-200 p-2 bg-white space-y-1.5" data-testid="ae-sender-picker">
            <p className="text-[11px] text-stone-600 font-medium">
              {lang === 'es' ? 'Remitente' : 'Sender'}
            </p>
            <select
              value={senderChoice}
              onChange={(e) => setSenderChoice(e.target.value)}
              className="w-full h-7 text-xs border border-stone-200 rounded-sm px-2 bg-white"
              data-testid="ae-sender-select"
            >
              <option value="">
                {lang === 'es' ? 'Por defecto' : 'Default'}{senderOptions.default?.email ? ` — ${senderOptions.default.email}` : ''}
              </option>
              {senderOptions.alternatives.map(a => (
                <option key={a.email} value={a.email}>{a.name} — {a.email}</option>
              ))}
              <option value="custom">{lang === 'es' ? 'Otro... (introducir)' : 'Other... (custom)'}</option>
            </select>
            {senderChoice === 'custom' && (
              <div className="grid grid-cols-2 gap-1.5">
                <Input value={customFromEmail} onChange={e => setCustomFromEmail(e.target.value)}
                  type="email"
                  placeholder={lang === 'es' ? 'Email autorizado' : 'Authorized email'}
                  className="h-7 text-xs" data-testid="ae-sender-custom-email" />
                <Input value={customFromName} onChange={e => setCustomFromName(e.target.value)}
                  placeholder={lang === 'es' ? 'Nombre visible' : 'Display name'}
                  className="h-7 text-xs" data-testid="ae-sender-custom-name" />
              </div>
            )}
            {senderChoice === 'custom' && (
              <p className="text-[10px] text-amber-700 italic">
                {lang === 'es'
                  ? 'Recuerda: el email tiene que estar autorizado en tu cuenta de Acumbamail.'
                  : 'Reminder: the email must be authorized in your Acumbamail account.'}
              </p>
            )}
          </div>
          {aePending.length === 0 ? (
            <p className="text-xs text-stone-400 text-center py-6">{lb.no_pending}</p>
          ) : (
            <>
              <div className="flex justify-between items-center text-[11px] text-stone-500">
                <span>{Object.values(aeSelected).filter(Boolean).length} / {aePending.length}</span>
                <div className="flex gap-2">
                  <button type="button" className="text-[11px] text-[#1f3e80] hover:underline"
                    onClick={() => { const s = {}; aePending.forEach(e => { if (e.event_key) s[e.event_key] = true; }); setAeSelected(s); }}>
                    {lb.select_all}
                  </button>
                  <button type="button" className="text-[11px] text-stone-500 hover:underline"
                    onClick={() => setAeSelected({})}>
                    {lb.deselect_all}
                  </button>
                </div>
              </div>
              <div className="max-h-[40vh] overflow-y-auto border border-stone-200 rounded-sm divide-y divide-stone-100">
                {aePending.map((ae) => (
                  <label key={ae.event_key} className="flex items-start gap-2 px-3 py-2 hover:bg-stone-50 cursor-pointer text-xs">
                    <Checkbox
                      checked={!!aeSelected[ae.event_key]}
                      onCheckedChange={(v) => setAeSelected(p => ({ ...p, [ae.event_key]: !!v }))}
                      data-testid={`ae-select-${ae.event_key}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] font-mono">{ae.volunteer_code}</Badge>
                        <Badge variant="outline" className="text-[10px] font-mono bg-blue-50 text-blue-700 border-blue-200">{ae.visit_code}</Badge>
                        <span className="text-[10px] text-stone-400">{ae.date ? new Date(ae.date).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US') : ''}</span>
                      </div>
                      <p className="text-[11px] text-stone-700 mt-0.5">{ae.question}</p>
                      {ae.observation && <p className="text-[11px] text-stone-500 italic">"{ae.observation.slice(0, 160)}{ae.observation.length > 160 ? '…' : ''}"</p>}
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNotifyDialogOpen(false)} data-testid="adverse-notify-cancel">
              {lb.cancel}
            </Button>
            <Button onClick={sendNotification} disabled={aeSending || Object.values(aeSelected).filter(Boolean).length === 0}
              data-testid="adverse-notify-send" className="bg-[#1f3e80] hover:bg-[#1a356c]">
              <EnvelopeSimple size={14} className="mr-1" />
              {aeSending ? lb.sending : lb.send_now}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Recipients Dialog ===== */}
      <Dialog open={recipientsDialogOpen} onOpenChange={setRecipientsDialogOpen}>
        <DialogContent data-testid="adverse-recipients-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{lb.recipients_dialog_title}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-500">{lb.recipients_dialog_desc}</p>
          <div>
            <p className="text-[11px] text-stone-500 mb-1">{lb.recipients_current}</p>
            {recipients.length === 0 ? (
              <p className="text-xs text-stone-400 italic px-1">{lb.no_recipients}</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {recipients.map(r => (
                  <Badge key={r} variant="outline" className="text-[11px] gap-1 py-1 pl-2 pr-1 bg-blue-50 border-blue-200 text-blue-800" data-testid={`recipient-${r}`}>
                    <span className="font-mono">{r}</span>
                    <button type="button" className="hover:bg-blue-200 rounded p-0.5"
                      onClick={() => saveRecipients(recipients.filter(x => x !== r))}
                      data-testid={`recipient-remove-${r}`}>
                      <X size={10} />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          {suggestedRecipients.filter(s => !recipients.includes(s)).length > 0 && (
            <div>
              <p className="text-[11px] text-stone-500 mb-1">{lb.recipients_suggested}</p>
              <div className="flex flex-wrap gap-1">
                {suggestedRecipients.filter(s => !recipients.includes(s)).map(s => (
                  <Button key={s} size="sm" variant="outline" className="h-6 px-2 text-[11px] gap-1"
                    onClick={() => saveRecipients([...recipients, s])} data-testid={`recipient-suggest-${s}`}>
                    + <span className="font-mono">{s}</span>
                  </Button>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-[11px] text-stone-500 mb-1">{lb.recipients_add}</p>
            <div className="flex gap-2">
              <Input type="email" value={newRecipient} onChange={e => setNewRecipient(e.target.value)}
                placeholder={lb.recipients_add_placeholder} className="h-8 text-xs"
                data-testid="recipient-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newRecipient.includes('@')) {
                    e.preventDefault();
                    saveRecipients([...recipients, newRecipient.trim().toLowerCase()]);
                    setNewRecipient('');
                  }
                }} />
              <Button size="sm" variant="outline" disabled={!newRecipient.includes('@') || recipientsSaving}
                onClick={() => { saveRecipients([...recipients, newRecipient.trim().toLowerCase()]); setNewRecipient(''); }}
                data-testid="recipient-add-btn">
                +
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecipientsDialogOpen(false)} data-testid="adverse-recipients-close">
              {lb.cancel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
